"use client";

import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface CommunitySearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onSubmit?: () => void;
  placeholder?: string;
  size?: "sm" | "default";
  className?: string;
  inputId?: string;
  showClear?: boolean;
}

const sizeStyles = {
  sm: {
    height: "h-12",
    text: "text-base",
    icon: "w-5 h-5",
    left: "left-4",
    pl: "pl-11",
    pr: "pr-11",
  },
  default: {
    height: "h-16",
    text: "text-xl",
    icon: "w-7 h-7",
    left: "left-5",
    pl: "pl-16",
    pr: "pr-16",
  },
};

export function CommunitySearchInput({
  value,
  onChange,
  onFocus,
  onSubmit,
  placeholder,
  size = "default",
  className,
  inputId = "community-search",
  showClear = true,
}: CommunitySearchInputProps) {
  const styles = sizeStyles[size];

  return (
    <div className={cn("relative", className)}>
      <label htmlFor={inputId} className="sr-only">
        {placeholder ?? "Search"}
      </label>
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 pointer-events-none z-10",
          styles.left,
        )}
      >
        <Search className={cn("text-brand-green", styles.icon)} />
      </div>
      <Input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onSubmit) {
            onSubmit();
          }
        }}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-xl border-brand-green-line/80 bg-panel shadow-[0_6px_18px_rgb(28_25_23/0.04)] transition-all hover:border-brand-green-line hover:shadow-[0_8px_22px_rgb(28_25_23/0.06)] focus:border-primary-500 focus:shadow-[0_8px_22px_rgb(37_137_34/0.07)] focus:ring-4 focus:ring-primary-500/5",
          styles.height,
          styles.text,
          styles.pl,
          styles.pr,
        )}
      />
      {showClear && value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-lg p-2 text-muted transition-colors hover:bg-raised hover:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
